import { useEffect, useState } from 'react'
import { useAuth } from '../../../context/EcoAppsAuthContext'
import { authService, rentalBizTelegramService, userService } from '../../../services/api'

const Settings = () => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    document_number: user?.document_number || '',
    address: user?.address || ''
  })
  const [preferences, setPreferences] = useState({
    email: true,
    payment_reminder_days: 5,
    contract_expiry_days: 30
  })
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordMessage, setPasswordMessage] = useState('')
  const [telegram, setTelegram] = useState({
    channelId: '',
    isEnabled: true,
    connected: false
  })
  const [telegramSaving, setTelegramSaving] = useState(false)
  const [telegramTesting, setTelegramTesting] = useState(false)
  const [telegramMessage, setTelegramMessage] = useState('')

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await userService.getById(user?.id)
        const profile = response?.data?.data || {}

        setFormData((prev) => ({
          ...prev,
          email: profile.email || user?.email || '',
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          phone: profile.phone || '',
          document_number: profile.document_number || '',
          address: profile.address || ''
        }))

        if (profile.notification_preferences) {
          try {
            const parsed = typeof profile.notification_preferences === 'string'
              ? JSON.parse(profile.notification_preferences)
              : profile.notification_preferences
            setPreferences((prev) => ({ ...prev, ...parsed }))
          } catch {
            // ignore malformed preferences JSON
          }
        }
      } catch {
        // fallback to auth context values
      }
    }

    if (user?.id) {
      loadProfile()
    }
  }, [user?.id, user?.email])

  useEffect(() => {
    const loadTelegram = async () => {
      try {
        const response = await rentalBizTelegramService.getStatus()
        const status = response?.data?.data || response?.data || {}
        setTelegram({
          channelId: status.channelId || status.chatId || '',
          isEnabled: status.isEnabled ?? true,
          connected: Boolean(status.connected)
        })
      } catch {
        // keep empty defaults
      }
    }

    loadTelegram()
  }, [])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setMessage('')
  }

  const handlePreferenceChange = (e) => {
    setPreferences({ ...preferences, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })
  }

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value })
    setPasswordMessage('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage('')

    try {
      await userService.update(user?.id, formData)
      setMessage('Perfil actualizado correctamente')
    } catch (error) {
      setMessage(error?.response?.data?.message || 'No fue posible actualizar el perfil')
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault?.()
    setPasswordMessage('')

    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      setPasswordMessage('Completa todos los campos de contraseña.')
      return
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMessage('La nueva contraseña y su confirmación no coinciden.')
      return
    }

    setPasswordSaving(true)
    try {
      const response = await authService.changePassword({
        currentPassword: passwordForm.current_password,
        newPassword: passwordForm.new_password
      })

      setPasswordMessage(response?.data?.data?.message || 'Contraseña actualizada correctamente.')
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })
    } catch (error) {
      const errorText =
        error?.response?.data?.errors?.join(', ') ||
        error?.response?.data?.message ||
        'No fue posible actualizar la contraseña.'
      setPasswordMessage(errorText)
    } finally {
      setPasswordSaving(false)
    }
  }

  const handleTelegramSubmit = async (e) => {
    e.preventDefault()
    setTelegramMessage('')

    if (!telegram.channelId.trim()) {
      setTelegramMessage('El Channel ID de Telegram es obligatorio.')
      return
    }

    setTelegramSaving(true)
    try {
      const response = await rentalBizTelegramService.connect(telegram.channelId, telegram.isEnabled)
      const status = response?.data?.data || response?.data || {}
      setTelegram({
        channelId: status.channelId || status.chatId || telegram.channelId,
        isEnabled: status.isEnabled ?? telegram.isEnabled,
        connected: Boolean(status.connected)
      })
      setTelegramMessage('Telegram guardado correctamente.')
    } catch (error) {
      setTelegramMessage(error?.response?.data?.message || 'No fue posible guardar Telegram.')
    } finally {
      setTelegramSaving(false)
    }
  }

  const handleTelegramTest = async () => {
    setTelegramMessage('')
    setTelegramTesting(true)
    try {
      await rentalBizTelegramService.test()
      setTelegramMessage('Mensaje de prueba enviado.')
    } catch (error) {
      setTelegramMessage(error?.response?.data?.message || 'No fue posible enviar la prueba.')
    } finally {
      setTelegramTesting(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div><h1 className="text-2xl font-bold text-gray-900">Configuracion</h1><p className="text-gray-500">Gestiona tu cuenta y preferencias</p></div>

      {message && <div className={`px-4 py-3 rounded-lg text-sm ${message.includes('correctamente') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{message}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Informacion Personal</h2>
          <div>
            <label htmlFor="email" className="label">Correo</label>
            <input id="email" name="email" type="email" value={formData.email} className="input bg-gray-50" disabled />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="first_name" className="label">Nombre</label>
              <input id="first_name" name="first_name" type="text" value={formData.first_name} onChange={handleChange} className="input" />
            </div>
            <div>
              <label htmlFor="last_name" className="label">Apellido</label>
              <input id="last_name" name="last_name" type="text" value={formData.last_name} onChange={handleChange} className="input" />
            </div>
          </div>
          <div>
            <label htmlFor="phone" className="label">Telefono</label>
            <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="input" />
          </div>
          <div>
            <label htmlFor="document_number" className="label">Cedula</label>
            <input id="document_number" name="document_number" type="text" value={formData.document_number} onChange={handleChange} className="input" />
          </div>
          <div>
            <label htmlFor="address" className="label">Direccion</label>
            <textarea id="address" name="address" value={formData.address} onChange={handleChange} className="input" rows={3} />
          </div>
        </div>

        <div className="card space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Preferencias de Notificaciones</h2>
          <div className="space-y-4">
            <div className="flex items-center">
              <input id="email_notify" name="email" type="checkbox" checked={preferences.email} onChange={handlePreferenceChange} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
              <label htmlFor="email_notify" className="ml-2 text-sm text-gray-700">Recibir notificaciones por email</label>
            </div>
            <div>
              <label htmlFor="payment_reminder_days" className="label">Dias de anticipacion para recordatorios de pago</label>
              <input id="payment_reminder_days" name="payment_reminder_days" type="number" min="1" max="30" value={preferences.payment_reminder_days} onChange={handlePreferenceChange} className="input w-32" />
            </div>
            <div>
              <label htmlFor="contract_expiry_days" className="label">Dias de anticipacion para contratos por vencer</label>
              <input id="contract_expiry_days" name="contract_expiry_days" type="number" min="1" max="90" value={preferences.contract_expiry_days} onChange={handlePreferenceChange} className="input w-32" />
            </div>
          </div>
        </div>

        <div className="card space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Telegram</h2>
            <p className="text-sm text-gray-500">Alertas 5 dias antes de finalizar contratos, renta y servicios.</p>
          </div>
          <div>
            <label htmlFor="telegram_channel_id" className="label">Channel ID</label>
            <input
              id="telegram_channel_id"
              type="text"
              value={telegram.channelId}
              onChange={(e) => {
                setTelegram((current) => ({ ...current, channelId: e.target.value }))
                setTelegramMessage('')
              }}
              className="input"
              placeholder="-1001234567890"
            />
          </div>
          <div className="flex items-center">
            <input
              id="telegram_enabled"
              type="checkbox"
              checked={telegram.isEnabled}
              onChange={(e) => setTelegram((current) => ({ ...current, isEnabled: e.target.checked }))}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="telegram_enabled" className="ml-2 text-sm text-gray-700">Activar alertas por Telegram</label>
          </div>

          {telegramMessage && (
            <div className={`px-4 py-3 rounded-lg text-sm ${telegramMessage.includes('correctamente') || telegramMessage.includes('enviado') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {telegramMessage}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button type="button" onClick={handleTelegramTest} disabled={telegramTesting || !telegram.connected} className="btn btn-secondary disabled:opacity-50">
              {telegramTesting ? 'Enviando...' : 'Enviar prueba'}
            </button>
            <button type="button" onClick={handleTelegramSubmit} disabled={telegramSaving} className="btn btn-primary disabled:opacity-50">
              {telegramSaving ? 'Guardando...' : 'Guardar Telegram'}
            </button>
          </div>
        </div>

        <div className="card space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Cambio de contraseña</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="current_password" className="label">Contraseña actual</label>
              <input
                id="current_password"
                name="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={handlePasswordChange}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="new_password" className="label">Nueva contraseña</label>
              <input
                id="new_password"
                name="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={handlePasswordChange}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="confirm_password" className="label">Confirmar nueva contraseña</label>
              <input
                id="confirm_password"
                name="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={handlePasswordChange}
                className="input"
              />
            </div>

            {passwordMessage && (
              <div className={`px-4 py-3 rounded-lg text-sm ${passwordMessage.includes('correctamente') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {passwordMessage}
              </div>
            )}

            <div className="flex justify-end">
              <button type="button" onClick={handlePasswordSubmit} disabled={passwordSaving} className="btn btn-secondary disabled:opacity-50">
                {passwordSaving ? 'Actualizando...' : 'Actualizar Contraseña'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn btn-primary disabled:opacity-50">{saving ? 'Guardando...' : 'Guardar Cambios'}</button>
        </div>
      </form>
    </div>
  )
}

export default Settings
